<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateCompany',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    role: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    create: {
      own: $fn.value.pgettext('Company|Header', 'New own company'),
      partner: $fn.value.pgettext('Company|Header', 'New partner'),
    },
    resourceTitle: {
      own: $fn.value.pgettext('Company|ResourceTitle', 'Company'),
      partner: $fn.value.pgettext('Company|ResourceTitle', 'Partner'),
    },
    submitButtonText: {
      own: $fn.value.pgettext('Button', 'Create company'),
      partner: $fn.value.pgettext('Button', 'Create partner'),
    },
  });
  const item = ref(null);
  const formName = ref('company');
  const formId = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    messages: messages.value,
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = async () => {
    return $fn.value.schemaDefaults('CreateCompany', {
      emails: null,
      role: props.role,
      relation: props.role === 'company' ? 'na' : 'customer',
    });
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: {},
    dataRefs: {
      messages,
      item,
      formName,
      formId,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.resourceTitle[props.role];
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: messages.value.create[props.role],
        icon: $config.value.company.icons[props.role],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.role,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('role');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_9swhby7cx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_4ajmin1l8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_vlgbv4adj_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.company',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadSlickgrid_0frl00ul8_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'companies',
    };
  };
  const act_1040167445267876_request_cmyyr3lw8_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_company',
      data: item.value,
    };
  };
  const act_5534025912102772_setData_90r941nax_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_9swhby7cx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_9swhby7cx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_p9ie162bq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_9swhby7cx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_p9ie162bq],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_tdhyxkt7k =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_bluci1z7a =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_tdhyxkt7k,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_bluci1z7a],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_thvxs4wna =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_yhldrl1lz =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_thvxs4wna,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_yhldrl1lz],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_4ajmin1l8 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_4ajmin1l8_getActionArgs,
    });
  const inst_evh_1040167445267876_success_c0zbpnoom =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_4ajmin1l8,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_vlgbv4adj =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_vlgbv4adj_getActionArgs,
    });
  const inst_evh_1040167445267876_success_wzj4rvzv =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_vlgbv4adj,
      event: 'success',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadSlickgrid_0frl00ul8 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_0frl00ul8_getActionArgs,
    });
  const inst_evh_1040167445267876_success_5ah79i792 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_0frl00ul8,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_request_cmyyr3lw8 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_cmyyr3lw8_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_success_c0zbpnoom,
      inst_evh_1040167445267876_success_wzj4rvzv,
      inst_evh_1040167445267876_success_5ah79i792,
    ],
  });
  const inst_evh_1040167445267876_submit_0dfyhad01 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_cmyyr3lw8,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_0dfyhad01],
    event: 'submit',
  });
  const act_5534025912102772_setData_90r941nax = new actionTypes.SetDataAction({
    actionArgs: act_5534025912102772_setData_90r941nax_getActionArgs,
  });
  const inst_evh_5534025912102772_input_9lwv3vxhr =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_setData_90r941nax,
      event: 'input',
      displayName: 'setData',
    });
  const inst_evh_5534025912102772_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_input_9lwv3vxhr],
    event: 'input',
  });
  const act_2434514588044777_closeComponent_lrwbadpyp =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_xgsb93xfq =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_lrwbadpyp,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_xgsb93xfq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_p9ie162bq,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_bluci1z7a,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_yhldrl1lz,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_c0zbpnoom,
    inst_evh_1040167445267876_success_wzj4rvzv,
    inst_evh_1040167445267876_success_5ah79i792,
    inst_evh_1040167445267876_submit_0dfyhad01,
    create: inst_evh_1040167445267876_submit_0dfyhad01,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_input_9lwv3vxhr,
    inst_evh_5534025912102772_input,
    inst_evh_2434514588044777_clickPreventStop_xgsb93xfq,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_p9ie162bq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_p9ie162bq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_bluci1z7a = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_bluci1z7a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_yhldrl1lz = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_yhldrl1lz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_c0zbpnoom = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_c0zbpnoom.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_wzj4rvzv = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_wzj4rvzv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_5ah79i792 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_5ah79i792.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_0dfyhad01 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_0dfyhad01.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_input_9lwv3vxhr = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input_9lwv3vxhr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_input = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_xgsb93xfq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_xgsb93xfq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateCompany"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.company.color"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateCompany"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetCompanyGeneral
          :item="item"
          display-fetch-by-tax-number
          @input="evh_5534025912102772_input($event, {})"
        >
        </FieldsetCompanyGeneral>
        <div v-if="item.addresses && item.addresses.length">
          <ItpText uppercase :text="pgettext('CreateCompany', 'Address')">
          </ItpText>
          <hr />
          <FieldsetAddress :address="item.addresses[0]"> </FieldsetAddress>
        </div>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="messages.submitButtonText[role]"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
